import type { ReactNode } from 'react'
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  KeyRound,
  RefreshCw,
  Sparkles,
} from 'lucide-react'
import { useSearchParams } from 'react-router'
import { ApiContractError, ApiError } from '@/api/client'
import type { KeywordAnalysisSort, KeywordAnalysisView } from '@/api/keywordAnalysis/api'
import type {
  KeywordAssignment,
  KeywordProducttype,
  KeywordType,
} from '@/api/keywordAnalysis/types'
import { useKeywordAnalysis } from '@/features/keywordAnalysis/useKeywordAnalysis'
import { KeywordAreaNav } from '@/features/keywords/KeywordAreaNav'

const keywordGroups = [
  {
    type: 0,
    label: 'Primär',
    accent: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/45',
  },
  {
    type: 1,
    label: 'Sekundär',
    accent: 'border-teal-200 bg-teal-50 dark:border-teal-900 dark:bg-teal-950/45',
  },
  {
    type: 2,
    label: 'Emotion',
    accent: 'border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/45',
  },
  {
    type: 3,
    label: 'Longtail',
    accent: 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/45',
  },
] as const satisfies readonly {
  readonly type: KeywordType
  readonly label: string
  readonly accent: string
}[]

const numberFormatter = new Intl.NumberFormat('de-DE')
const pageSizeOptions = [10, 25, 50, 100, 500] as const
type PageSize = (typeof pageSizeOptions)[number]

function parsePage(value: string | null): number {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
}

function parsePageSize(value: string | null): PageSize {
  const parsed = Number(value)
  return pageSizeOptions.find((option) => option === parsed) ?? 10
}

function parseSort(value: string | null): KeywordAnalysisSort {
  switch (value) {
    case 'traffic':
    case 'secondary-traffic':
    case 'emotion-traffic':
    case 'longtail-traffic':
    case 'alias-traffic':
      return value
    default:
      return 'sales'
  }
}

function parseView(value: string | null): KeywordAnalysisView {
  switch (value) {
    case 'without-primary':
    case 'without-secondary':
    case 'without-emotion':
    case 'without-alias':
      return value
    default:
      return 'all'
  }
}

function totalTraffic(keywords: readonly KeywordAssignment[]): number {
  return keywords.reduce((total, keyword) => total + (keyword.searchVolumePerMonthAvg ?? 0), 0)
}

function KeywordGroupStats({ keywords }: { readonly keywords: readonly KeywordAssignment[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
      <span>{numberFormatter.format(keywords.length)} Keywords</span>
      <span aria-hidden="true">•</span>
      <span>{numberFormatter.format(totalTraffic(keywords))} Traffic</span>
    </div>
  )
}

function PaginationControls({
  page,
  pageSize,
  sort,
  view,
  totalPages,
  hasNextPage,
  position,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onViewChange,
}: {
  readonly page: number
  readonly pageSize: PageSize
  readonly sort: KeywordAnalysisSort
  readonly view: KeywordAnalysisView
  readonly totalPages: number | null
  readonly hasNextPage: boolean
  readonly position: 'oben' | 'unten'
  readonly onPageChange: (page: number) => void
  readonly onPageSizeChange: (pageSize: PageSize) => void
  readonly onSortChange: (sort: KeywordAnalysisSort) => void
  readonly onViewChange: (view: KeywordAnalysisView) => void
}): ReactNode {
  return (
    <nav
      aria-label={`Seitennavigation ${position}`}
      className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900"
    >
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => {
          onPageChange(page - 1)
        }}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
      >
        <ChevronLeft aria-hidden="true" size={17} />
        Zurück
      </button>

      <div className="flex flex-wrap items-center justify-center gap-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
          Ansicht
          <select
            value={view}
            onChange={(event) => {
              onViewChange(parseView(event.target.value))
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="all">Alle Produkttypen</option>
            <option value="without-primary">Ohne Primär-Keywords</option>
            <option value="without-secondary">Ohne Sekundär-Keywords</option>
            <option value="without-emotion">Ohne Emotion-Keywords</option>
            <option value="without-alias">Ohne Alias-Keywords</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
          Sortieren nach
          <select
            value={sort}
            onChange={(event) => {
              onSortChange(parseSort(event.target.value))
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="sales">Verkäufe 365 Tage</option>
            <option value="traffic">Primär-Traffic</option>
            <option value="secondary-traffic">Sekundär-Traffic</option>
            <option value="emotion-traffic">Emotion-Traffic</option>
            <option value="longtail-traffic">Longtail-Traffic</option>
            <option value="alias-traffic">Alias-Traffic</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
          Pro Seite
          <select
            value={pageSize}
            onChange={(event) => {
              onPageSizeChange(parsePageSize(event.target.value))
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          Seite {String(page)}
          {totalPages === null ? '' : ` von ${String(totalPages)}`}
        </p>
      </div>

      <button
        type="button"
        disabled={!hasNextPage}
        onClick={() => {
          onPageChange(page + 1)
        }}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
      >
        Weiter
        <ChevronRight aria-hidden="true" size={17} />
      </button>
    </nav>
  )
}

function KeywordPills({
  keywords,
}: {
  readonly keywords: readonly KeywordAssignment[]
}): ReactNode {
  if (keywords.length === 0) {
    return <span className="text-sm text-slate-400 dark:text-slate-500">Noch keine Keywords</span>
  }

  return (
    <div className="flex flex-wrap gap-2">
      {keywords.map((keyword) => (
        <span
          key={keyword.keywordId}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/80 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          {keyword.value}
          {keyword.languageKey === 'de' ? null : (
            <span className="text-[10px] font-semibold uppercase text-slate-400">
              {keyword.languageKey}
            </span>
          )}
          {keyword.searchVolumePerMonthAvg === null ? null : (
            <span className="text-[10px] font-semibold tabular-nums text-slate-400">
              {numberFormatter.format(keyword.searchVolumePerMonthAvg)}
            </span>
          )}
        </span>
      ))}
    </div>
  )
}

export function ProducttypeCard({
  producttype,
}: {
  readonly producttype: KeywordProducttype
}): ReactNode {
  const aliases = producttype.keywords.filter((keyword) => keyword.isAlias)
  const primaryKeywords = producttype.keywords.filter((keyword) => keyword.keywordType === 0)
  const producttypeTraffic = totalTraffic(primaryKeywords)
  const editUrl = `https://panda-weltmaschine.de/product-data/producttypes?init_key=${encodeURIComponent(producttype.producttypeKey)}`

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <header className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-700">
        <div className="flex items-center gap-3">
          <span className="rounded-lg bg-slate-950 px-3 py-2 font-mono text-sm font-black tracking-wide text-white dark:bg-slate-100 dark:text-slate-950">
            {producttype.producttypeKey}
          </span>
          <div>
            <h2 className="font-bold text-slate-950 dark:text-white">
              {producttype.producttypeName}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {numberFormatter.format(producttype.keywords.length)} Keyword-Zuordnungen
              <span aria-hidden="true"> · </span>
              {numberFormatter.format(producttypeTraffic)} Primär-Traffic
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled
            title="Diese Funktion wird später ergänzt."
            className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm font-semibold text-violet-500 opacity-70 dark:border-violet-900 dark:bg-violet-950/45 dark:text-violet-300"
          >
            <Sparkles aria-hidden="true" size={15} />
            Neue Keywords anfordern
          </button>
          <a
            href={editUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            In Weltmaschine bearbeiten
            <ExternalLink aria-hidden="true" size={15} />
          </a>
          <div className="rounded-xl bg-emerald-50 px-4 py-2 text-right dark:bg-emerald-950/50">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              Verkäufe 365 Tage
            </p>
            <p className="text-xl font-black tabular-nums text-emerald-900 dark:text-emerald-100">
              {numberFormatter.format(producttype.sales365)}
            </p>
          </div>
        </div>
      </header>

      <div className="grid gap-3 p-4 lg:grid-cols-2">
        {keywordGroups.map((group) => (
          <KeywordGroup
            key={group.type}
            label={group.label}
            accent={group.accent}
            keywords={producttype.keywords.filter((keyword) => keyword.keywordType === group.type)}
          />
        ))}

        <section className="rounded-xl border border-violet-200 bg-violet-50 p-4 lg:col-span-2 dark:border-violet-900 dark:bg-violet-950/45">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-violet-700 dark:text-violet-300">
              Alias-Keywords
            </h3>
            <KeywordGroupStats keywords={aliases} />
          </div>
          <KeywordPills keywords={aliases} />
        </section>
      </div>
    </article>
  )
}

function KeywordGroup({
  label,
  accent,
  keywords,
}: {
  readonly label: string
  readonly accent: string
  readonly keywords: readonly KeywordAssignment[]
}): ReactNode {
  return (
    <section className={`rounded-xl border p-4 ${accent}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
          {label}
        </h3>
        <KeywordGroupStats keywords={keywords} />
      </div>
      <KeywordPills keywords={keywords} />
    </section>
  )
}

export function KeywordAnalysisPage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = parsePage(searchParams.get('page'))
  const pageSize = parsePageSize(searchParams.get('limit'))
  const sort = parseSort(searchParams.get('sort'))
  const view = parseView(searchParams.get('view'))
  const { data, isLoading, isFetching, error, refetch } = useKeywordAnalysis(
    page,
    pageSize,
    sort,
    view,
  )
  const items = data?.items ?? []
  const totalPages =
    data?.totalCount === null || data?.totalCount === undefined
      ? null
      : Math.max(1, Math.ceil(data.totalCount / pageSize))
  const hasNextPage = totalPages === null ? items.length === pageSize : page < totalPages

  const writeSearchParams = (
    nextPage: number,
    nextPageSize: PageSize,
    nextSort: KeywordAnalysisSort,
    nextView: KeywordAnalysisView,
  ): void => {
    const nextParams = new URLSearchParams()
    if (nextPage > 1) nextParams.set('page', String(nextPage))
    if (nextPageSize !== 10) nextParams.set('limit', String(nextPageSize))
    if (nextSort !== 'sales') nextParams.set('sort', nextSort)
    if (nextView !== 'all') nextParams.set('view', nextView)
    setSearchParams(nextParams)
  }

  const changePage = (nextPage: number): void => {
    writeSearchParams(nextPage, pageSize, sort, view)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const changePageSize = (nextPageSize: PageSize): void => {
    writeSearchParams(1, nextPageSize, sort, view)
  }

  const changeSort = (nextSort: KeywordAnalysisSort): void => {
    writeSearchParams(1, pageSize, nextSort, view)
  }

  const changeView = (nextView: KeywordAnalysisView): void => {
    writeSearchParams(1, pageSize, sort, nextView)
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <KeywordAreaNav />
      <section className="rounded-3xl bg-gradient-to-br from-indigo-950 via-slate-950 to-violet-950 px-6 py-8 text-white shadow-xl sm:px-10 sm:py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-violet-300/25 bg-violet-300/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-violet-200">
              <KeyRound aria-hidden="true" size={15} />
              Keyword-Analyse
            </div>
            <h1 className="mt-5 text-3xl font-black tracking-tight sm:text-4xl">
              Produkttypen und ihre Keyword-Struktur
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
              Nach Verkäufen der letzten 365 Tage sortiert. Primär, Sekundär, Emotion, Longtail und
              Aliase sind klar getrennt und für die spätere Bearbeitung vorbereitet.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              void refetch()
            }}
            className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15 lg:self-auto"
          >
            <RefreshCw
              aria-hidden="true"
              size={16}
              className={isFetching ? 'animate-spin' : undefined}
            />
            Aktualisieren
          </button>
        </div>
      </section>

      {error !== null ? (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <div className="flex items-start gap-2">
            <AlertTriangle aria-hidden="true" size={19} className="mt-0.5 shrink-0" />
            <p className="text-sm font-medium">
              {error instanceof ApiError || error instanceof ApiContractError
                ? error.message
                : 'Die Keyword-Analyse konnte nicht geladen werden.'}
            </p>
          </div>
        </div>
      ) : null}

      <div className="mt-6">
        <PaginationControls
          page={page}
          pageSize={pageSize}
          sort={sort}
          view={view}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          position="oben"
          onPageChange={changePage}
          onPageSizeChange={changePageSize}
          onSortChange={changeSort}
          onViewChange={changeView}
        />
      </div>

      <div className="mt-4 space-y-4">
        {isLoading ? (
          Array.from({ length: 3 }, (_, index) => (
            <div
              key={index}
              className="h-64 animate-pulse rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
            />
          ))
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-700 dark:bg-slate-900">
            <p className="font-semibold text-slate-800 dark:text-slate-100">
              Keine Produkttypen auf dieser Seite gefunden.
            </p>
          </div>
        ) : (
          items.map((producttype) => (
            <ProducttypeCard key={producttype.producttypeKey} producttype={producttype} />
          ))
        )}
      </div>

      <div className="mt-7">
        <PaginationControls
          page={page}
          pageSize={pageSize}
          sort={sort}
          view={view}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          position="unten"
          onPageChange={changePage}
          onPageSizeChange={changePageSize}
          onSortChange={changeSort}
          onViewChange={changeView}
        />
      </div>
    </div>
  )
}
